/**
 * Les catégories de la boîte à outils, et ce que chacune contient.
 *
 * **Une seule source, parce qu'il y en avait deux.** L'appartenance d'un outil à une catégorie
 * était écrite dans le registre de `ToolsView.js` *et* recopiée à la main dans la barre latérale
 * de `App.js` — cette dernière la répétant même deux fois par catégorie, une pour surligner le
 * titre et une pour surligner l'entrée. Six blocs de JSX quasi identiques, que rien ne tenait
 * ensemble : déplacer un outil demandait trois modifications concordantes, et n'en oublier
 * aucune ne se vérifiait qu'à l'œil.
 *
 * Le registre de `ToolsView.js` garde ce qui lui est propre — description, statut, liste des
 * fonctionnalités —, mais lit ici sa catégorie.
 *
 * L'icône est stockée comme **composant**, non comme élément : la grille la rend en 24 px et la
 * barre latérale en 18 px. Un élément déjà construit imposerait une taille aux deux.
 */

import {
  Activity, Ban, Bot, Globe, Layers, Link2, Search,
  Smartphone, Sparkles, Store, Tag, TrendingUp
} from 'lucide-react';

/**
 * Quatre catégories, dans l'ordre d'affichage : les leviers payants, puis les gratuits.
 *
 * `sidebar: false` retire l'outil du menu sans le retirer de la catégorie : les deux variantes
 * par régie des termes à exclure ne s'atteignent que par leur hub, et les afficher côte à côte
 * ferait trois entrées de même nom.
 */
export const CATEGORIES_OUTILS = [
  {
    titre: 'SEA',
    Icon: Search,
    outils: [
      { id: 'diagnostic', libelle: 'Diagnostic Google Ads', Icon: Activity },
      { id: 'negative-keywords-hub', libelle: 'Termes à Exclure', Icon: Ban },
      { id: 'negative-keywords', libelle: 'Termes à Exclure (Google Ads)', Icon: Ban, sidebar: false },
      { id: 'microsoft-negative-keywords', libelle: 'Termes à Exclure (Microsoft Ads)', Icon: Ban, sidebar: false },
      { id: 'keyword-opportunities', libelle: "Détecteur d'Opportunités", Icon: Search },
      { id: 'keyword-planner', libelle: 'Idées de Mots-Clés', Icon: Sparkles },
      { id: 'bid-optimizer', libelle: "Optimiseur d'Enchères", Icon: TrendingUp },
      { id: 'device-bid-adjustments', libelle: 'Ajusteur par Appareils', Icon: Smartphone },
      // À cheval sur les deux leviers, rangé du côté qu'il fait économiser : il sert à couper
      // une dépense publicitaire sur une requête déjà tenue en organique.
      { id: 'sea-seo-overlap', libelle: 'Cannibalisation SEA/SEO', Icon: Layers },
      /**
       * Le flux marchand forme un sous-ensemble à part : on y entre pour une raison — un flux à
       * corriger —, et les trois outils se suivent alors. Les mêler aux mots-clés et aux enchères
       * obligeait à les retrouver un par un dans une liste de dix.
       *
       * Le bloc ferme la catégorie, et ce n'est pas un détail de présentation : un outil déclaré
       * après lui ouvrirait un troisième groupe, sans titre, sous l'intertitre « Shopping » — il
       * passerait pour en faire partie tout en n'y étant pas.
       */
      { id: 'product-labeling', libelle: 'Étiquetage Produits', Icon: Tag, sousCategorie: 'Shopping' },
      { id: 'feed-diagnostics', libelle: 'Diagnostic du Flux', Icon: Activity, sousCategorie: 'Shopping' },
      { id: 'feed-optimizer', libelle: 'Optimiseur de Flux', Icon: Store, sousCategorie: 'Shopping' }
    ]
  },
  {
    titre: 'SEO',
    Icon: Globe,
    outils: [
      { id: 'diagnostic-seo', libelle: 'Diagnostic SEO', Icon: Search },
      // Il relève aussi les AI Overviews, mais son objet reste la position organique : c'est
      // par elle qu'on vient le consulter.
      { id: 'serp-tracking', libelle: 'SERP Tracking', Icon: Globe },
      { id: 'backlinks', libelle: 'Backlinks', Icon: Link2 }
    ]
  },
  {
    titre: 'GEO',
    Icon: Bot,
    outils: [
      { id: 'ai-visibility', libelle: 'AI Visibility', Icon: Bot }
    ]
  },
  {
    titre: 'Social Ads',
    Icon: Layers,
    outils: [
      { id: 'competitor-ads', libelle: 'Veille Concurrentielle', Icon: Layers }
    ]
  }
];

/** L'ordre d'affichage des catégories dans le catalogue, dérivé de la structure ci-dessus. */
export const ORDRE_CATEGORIES = CATEGORIES_OUTILS.map(c => c.titre);

/**
 * Catégorie et sous-catégorie d'un outil, par son identifiant.
 *
 * Un identifiant inconnu rend `undefined` plutôt que de se rabattre sur une catégorie par
 * défaut : un outil ajouté au registre sans être rangé ici doit se voir, pas se cacher dans
 * « Divers ».
 */
const parIdentifiant = new Map(
  CATEGORIES_OUTILS.flatMap(c => c.outils.map(o => [o.id, { categorie: c.titre, sousCategorie: o.sousCategorie }]))
);

export const categorieDeLOutil = (id) => parIdentifiant.get(id)?.categorie;
export const sousCategorieDeLOutil = (id) => parIdentifiant.get(id)?.sousCategorie;

/**
 * Sous-catégories connues, et la catégorie dont chacune relève.
 *
 * Sert au cadrage du catalogue et au fil d'Ariane, qui reçoivent un libellé seul et doivent
 * savoir s'il désigne un niveau ou l'autre. Dérivée de la structure : une sous-catégorie
 * ajoutée plus haut est connue ici sans rien écrire de plus.
 */
const parentDeLaSousCategorie = new Map(
  CATEGORIES_OUTILS.flatMap(c => c.outils.filter(o => o.sousCategorie).map(o => [o.sousCategorie, c.titre]))
);

export const estUneSousCategorie = (libelle) => parentDeLaSousCategorie.has(libelle);
export const categorieParenteDe = (sousCategorie) => parentDeLaSousCategorie.get(sousCategorie);

/**
 * Rang d'un outil dans la déclaration ci-dessus.
 *
 * Le catalogue tient son propre registre, dans un ordre qui lui est historique ; sans ce tri, les
 * deux écrans présentent la même structure dans deux ordres différents.
 */
const rangs = new Map(CATEGORIES_OUTILS.flatMap(c => c.outils).map((o, rang) => [o.id, rang]));

export const rangDeLOutil = (id) => (rangs.has(id) ? rangs.get(id) : Number.MAX_SAFE_INTEGER);

/**
 * Les outils d'une catégorie, découpés en blocs par sous-catégorie. Un bloc sans sous-catégorie
 * porte `titre: null`.
 *
 * **Un seul bloc par sous-catégorie**, placé là où elle apparaît pour la première fois — et non
 * un bloc par suite d'entrées voisines. La version par consécutivité tenait dans la barre
 * latérale, qui lit la déclaration ci-dessus, mais pas dans le catalogue, qui parcourt son propre
 * registre : les trois outils Shopping n'y étant pas côte à côte, l'intertitre « Shopping »
 * s'affichait trois fois, chaque fois avec un seul outil, et les autres outils du levier
 * semblaient rangés dedans.
 *
 * Écrit ici parce que la barre latérale et le catalogue en ont tous deux besoin, et que les deux
 * doivent présenter le même découpage — c'est précisément ce genre de calcul recopié qui avait
 * fait diverger les catégories avant leur regroupement dans ce module.
 */
export const grouperParSousCategorie = (outils) => {
  const groupes = [];
  const parTitre = new Map();

  for (const outil of [...outils].sort((a, b) => rangDeLOutil(a.id) - rangDeLOutil(b.id))) {
    const titre = outil.sousCategorie || null;
    const existant = parTitre.get(titre);

    if (existant) {
      existant.outils.push(outil);
      continue;
    }

    const groupe = { titre, outils: [outil] };
    parTitre.set(titre, groupe);
    groupes.push(groupe);
  }

  return groupes;
};
